import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Alert
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { getWorkbookId, getCachedData } from '../../services/plmsService';
import { DashboardMetrics, OnboardingItem } from '../../types/plms';
import { RefreshCw, CheckCircle2, Calendar, Clock, BookOpen, ListChecks } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }: { title: string; value: string | number; icon: any; color: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
    <Box sx={{ 
      p: 1,
      borderRadius: 1,
      bgcolor: `${color}15`,
      color: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Icon size={20} />
    </Box>
    <Box>
      <Typography color="text.secondary" variant="body2">
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [nextItems, setNextItems] = useState<OnboardingItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      setError(null);
      
      const workbookId = await getWorkbookId(user.email);
      if (!workbookId) {
        setError('No workbook found');
        return;
      }

      // First, load metrics specifically for the dashboard
      const { items = [], metrics: dashboardMetrics } = await getCachedData(user.email, workbookId);
      
      // Set metrics first if available
      if (dashboardMetrics) {
        setMetrics(dashboardMetrics);
      } else {
        console.warn('No metrics data available');
        setMetrics({
          name: user.email.split('@')[0],
          startDate: new Date().toISOString(),
          doneItems: 0,
          itemsRemaining: 0,
          percentDone: 0,
          itemsDoneLastWeek: 0,
          shouldBeOnDay: 0,
          actuallyOnDay: 0,
          daysLeft: 0,
          lastItemDoneDaysAgo: 0,
          latestDoneItem: '',
          toBePasted: 0
        });
      }

      // Then handle items
      if (Array.isArray(items)) {
        const incomplete = items
          .filter(item => !item.dateDone)
          .sort((a, b) => (a.day - b.day))
          .slice(0, 10);
        setNextItems(incomplete);
      } else {
        console.error('Items is not an array:', items);
        setNextItems([]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
      setNextItems([]);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [user?.email, setMetrics, setNextItems, setError, setLoading]);

  // Load data when component mounts or user changes
  useEffect(() => {
    if (user?.email) {
      loadData();
    }
  }, [user?.email, loadData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Format the start date safely
  const formattedStartDate = metrics?.startDate ? 
    new Date(metrics.startDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : 'Not set';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>{metrics?.name}'s Dashboard</Typography>
          <Typography variant="body1" color="text.secondary">
            Started on {formattedStartDate}
          </Typography>
        </Box>
        <IconButton onClick={loadData} size="large">
          <RefreshCw />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Progress and Stats */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Progress circle */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 2 }}>
                  <Typography variant="h6">Overall Progress</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 'auto',
                      color: (metrics?.actuallyOnDay || 0) < (metrics?.shouldBeOnDay || 0) ? 'error.main' : 'success.main'
                    }}
                  >
                    {(metrics?.actuallyOnDay || 0) < (metrics?.shouldBeOnDay || 0) ? 'Behind schedule' : 'On track'}
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', display: 'inline-flex', my: 2 }}>
                  <CircularProgress
                    variant="determinate"
                    value={metrics?.percentDone || 0}
                    size={140}
                    thickness={4}
                    sx={{ color: 'primary.main' }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h4" component="div">
                      {Math.round(metrics?.percentDone || 0)}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Item stats */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Items Overview</Typography>
                <Stack spacing={3}>
                  <StatCard
                    title="Done Items"
                    value={metrics?.doneItems || 0}
                    icon={ListChecks}
                    color="#2e7d32"
                  />
                  <StatCard
                    title="Items Remaining"
                    value={metrics?.itemsRemaining || 0}
                    icon={BookOpen}
                    color="#ed6c02"
                  />
                  <StatCard
                    title="To Be Pasted"
                    value={metrics?.toBePasted || 0}
                    icon={Clock}
                    color="#0288d1"
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Activity metrics */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 3, 
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ 
                    flex: '1 1 auto', 
                    minWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <CheckCircle2 
                      size={24} 
                      color="#2e7d32" 
                      style={{ marginBottom: '8px' }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Items Done Last Week
                    </Typography>
                    <Typography variant="h6">
                      {metrics?.itemsDoneLastWeek || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: '1 1 auto', 
                    minWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Calendar 
                      size={24} 
                      color="#ed6c02" 
                      style={{ marginBottom: '8px' }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Days Left
                    </Typography>
                    <Typography variant="h6">
                      {metrics?.daysLeft || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: '1 1 auto', 
                    minWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Clock 
                      size={24} 
                      color="#0288d1" 
                      style={{ marginBottom: '8px' }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Last Activity
                    </Typography>
                    <Typography variant="h6">
                      {metrics?.lastItemDoneDaysAgo || 0} days ago
                    </Typography>
                  </Box>
                </Box>
                {metrics?.latestDoneItem && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mt: 3, textAlign: 'center' }}
                  >
                    Latest completed: {metrics.latestDoneItem}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - Next Items */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Next Items</Typography>
            <List>
              {nextItems.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.item}
                      secondary={`Day ${item.day} - ${item.section}`}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        fontWeight: 500
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption'
                      }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export const PLMSDashboard = Dashboard;
export default Dashboard;
